import * as chatApi from "@lib/chatApi";
import { reportError } from "@lib/graphql/unwrapMutation";
import { track } from "@lib/telemetry";
import type {
  ChatAttachment,
  ChatMessage,
  ChatPlan,
  ChatQuestion,
  ChatRichTextArtefact,
  ChatTask,
  ChatThread,
} from "@lib/types";
import { create } from "zustand";

/** Health of the ChatChannel subscription, mirrored from ActionCable callbacks. */
export type ChatConnectionState = "connected" | "reconnecting" | "rejected";

/** An assistant reply arriving as `message_delta` broadcasts (#3162). The
 *  buffer is ephemeral: the persisted `new_message` with the same id replaces
 *  it, and anything that could have missed a delta (a gap, a reconnect, a
 *  resync) discards it and waits for that message. */
export interface StreamingReply {
  messageId: string;
  /** Last delta seq applied — the stream's own counter, not the thread's broadcast_seq. */
  seq: number;
  text: string;
  /** Wall-clock ms of the last delta; drives the 15 s stall timeout. */
  lastDeltaAt: number;
}

function withoutKey<T>(record: Record<string, T>, key: string): Record<string, T> {
  if (!(key in record)) return record;
  const { [key]: _dropped, ...rest } = record;
  return rest;
}

// A seq gap can be two broadcasts committed in one transaction arriving
// swapped; wait briefly before fetching so the pair can fill itself in.
const RESYNC_GAP_DEBOUNCE_MS = 300;
let gapResyncTimer: ReturnType<typeof setTimeout> | undefined;

interface ViewContext {
  view: string;
  resource_type?: string;
  resource_id?: string;
  campaign_id?: string;
  outreach_programme_id?: string;
}

interface ChatState {
  isOpen: boolean;
  sessionId: string | null;
  threads: ChatThread[];
  currentThreadId: string | null;
  /** When no thread is selected: true = history list, false = composer (default). */
  showHistory: boolean;
  messages: ChatMessage[];
  isLoading: boolean;
  isSending: boolean;
  viewContext: ViewContext;
  /** Incremented each time a tool finishes executing — views subscribe to trigger refetches */
  dataVersion: number;
  panelWidth: number;
  /** Per-thread active ChatPlan (in-flight states only). Updated via broadcast. */
  plansByThread: Record<string, ChatPlan | null>;
  /** Per-thread pending ChatQuestion (open state only). Updated via broadcast. */
  questionsByThread: Record<string, ChatQuestion | null>;
  /** Per-thread agent task list. Updated via broadcast. */
  taskListByThread: Record<string, ChatTask[]>;
  /** The user message id whose completion this tab has already reported as
   *  rendered, per thread — so `turn_rendered` fires once per turn (#3161). */
  renderedTurnByThread: Record<string, string>;
  /** Retry payloads for optimistic messages whose send failed, keyed by the
   *  temp message id. Lets the message list offer a working retry affordance. */
  failedSends: Record<
    string,
    {
      threadId: string;
      message: string;
      attachments?: ChatAttachment[];
      richTextArtefacts?: ChatRichTextArtefact[];
    }
  >;
  /** ActionCable subscription health; drives the header "Reconnecting…" pill. */
  connectionState: ChatConnectionState;
  /** Highest broadcast seq seen per thread — stale/gap detection (#3160). */
  lastSeqByThread: Record<string, number>;
  /** In-flight streamed assistant reply per thread (#3162). */
  streamingByThread: Record<string, StreamingReply>;

  // UI actions
  toggle: () => void;
  open: () => void;
  close: () => void;
  setViewContext: (ctx: ViewContext) => void;
  setPanelWidth: (width: number) => void;
  setShowHistory: (value: boolean) => void;
  startNewChat: () => void;

  // Thread selection
  setCurrentThread: (threadId: string | null) => void;
  closeCurrentThread: () => void;

  // Real-time updates
  addMessage: (message: ChatMessage) => void;
  updateThreadStatus: (
    threadId: string,
    status: string,
    phase: string,
    toolName?: string,
    activePlan?: ChatPlan | null,
    pendingQuestion?: ChatQuestion | null,
    planModeEnabled?: boolean,
    taskList?: ChatTask[],
  ) => void;
  updateThreadTitle: (threadId: string, title: string) => void;
  setPlanModeEnabled: (threadId: string, enabled: boolean) => void;
  setConnectionState: (state: ChatConnectionState) => void;
  /** Records a broadcast's seq for its thread. Returns false when the broadcast
   *  is stale (seq at or below the highest already seen) and must be dropped.
   *  A gap schedules an incremental resync of the open thread. */
  acceptBroadcast: (threadId: string, seq?: number) => boolean;
  /** Applies a `message_delta`. Starts a buffer only at seq 1, appends only
   *  the next seq in order; anything else discards the buffer so a partial
   *  reply is never shown with a hole in it. */
  appendStreamDelta: (threadId: string, messageId: string, seq: number, text: string) => void;
  clearStreaming: (threadId: string) => void;

  // API actions
  fetchThreads: () => Promise<void>;
  fetchMessages: (threadId: string) => Promise<void>;
  /** Incremental catch-up for the open thread: current status plus messages
   *  after the last persisted one. Used after reconnect, tab focus, a seq gap
   *  and the activity timeout. No-op unless threadId is the open thread. */
  resyncThread: (threadId: string) => Promise<void>;
  createThread: (
    message: string,
    attachments?: ChatAttachment[],
    planModeEnabled?: boolean,
    richTextArtefacts?: ChatRichTextArtefact[],
  ) => Promise<void>;
  sendMessage: (
    threadId: string,
    message: string,
    attachments?: ChatAttachment[],
    richTextArtefacts?: ChatRichTextArtefact[],
  ) => Promise<void>;
  interruptThread: (threadId: string) => Promise<void>;
  compactThread: (threadId: string) => Promise<void>;
  /** Re-send an optimistic message whose original send failed. Removes the
   *  failed bubble first so a successful retry never leaves a duplicate. */
  retryMessage: (tempId: string) => Promise<void>;
}

function readPersistedBool(key: string, defaultValue = false): boolean {
  try {
    const val = localStorage.getItem(key);
    if (val === null) return defaultValue;
    return val === "true";
  } catch {
    return defaultValue;
  }
}

function readPersistedString(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function readPersistedNumber(key: string, defaultValue: number): number {
  try {
    const val = localStorage.getItem(key);
    if (val === null) return defaultValue;
    const num = Number(val);
    return Number.isFinite(num) ? num : defaultValue;
  } catch {
    return defaultValue;
  }
}

export const useChatStore = create<ChatState>((set, get) => ({
  isOpen: readPersistedBool("chat-open", true),
  sessionId: null,
  threads: [],
  currentThreadId: readPersistedString("chat-thread-id"),
  showHistory: false,
  messages: [],
  isLoading: false,
  isSending: false,
  viewContext: { view: "dashboard" },
  dataVersion: 0,
  panelWidth: readPersistedNumber("chat-panel-width", 480),
  plansByThread: {},
  questionsByThread: {},
  taskListByThread: {},
  failedSends: {},
  renderedTurnByThread: {},
  connectionState: "connected",
  lastSeqByThread: {},
  streamingByThread: {},

  toggle: () => {
    const wasOpen = get().isOpen;
    const nextOpen = !wasOpen;
    set({ isOpen: nextOpen });
    localStorage.setItem("chat-open", String(nextOpen));
    if (nextOpen) get().fetchThreads();
  },
  open: () => {
    set({ isOpen: true });
    localStorage.setItem("chat-open", "true");
    get().fetchThreads();
  },
  close: () => {
    set({ isOpen: false });
    localStorage.setItem("chat-open", "false");
  },
  setViewContext: (ctx) => {
    const prev = get().viewContext;
    set({ viewContext: ctx });
    if (ctx.campaign_id !== prev.campaign_id && get().isOpen) {
      get().fetchThreads();
    }
  },
  setPanelWidth: (width) => {
    const clamped = Math.max(320, Math.min(720, width));
    set({ panelWidth: clamped });
    localStorage.setItem("chat-panel-width", String(clamped));
  },

  setShowHistory: (value) => {
    set({ showHistory: value });
    if (value) get().fetchThreads();
  },

  startNewChat: () => {
    set({ currentThreadId: null, showHistory: false, messages: [] });
    localStorage.removeItem("chat-thread-id");
  },

  setCurrentThread: (threadId) => {
    set({ currentThreadId: threadId, messages: [], showHistory: false, streamingByThread: {} });
    localStorage.setItem("chat-thread-id", threadId ?? "");
    if (threadId) get().fetchMessages(threadId);
  },

  closeCurrentThread: () => {
    set({ currentThreadId: null, messages: [], streamingByThread: {} });
    localStorage.removeItem("chat-thread-id");
    if (get().showHistory) get().fetchThreads();
  },

  addMessage: (message) => {
    set((s) => {
      // The persisted message supersedes its streamed buffer (#3162).
      const streamingByThread =
        s.streamingByThread[message.thread_id]?.messageId === message.id
          ? withoutKey(s.streamingByThread, message.thread_id)
          : s.streamingByThread;
      // Only add if viewing this thread
      if (s.currentThreadId !== message.thread_id) return { streamingByThread };
      // Avoid duplicates by real ID
      if (s.messages.some((m) => m.id === message.id)) return { streamingByThread };
      // Replace optimistic temp message with matching content/role
      const filtered = s.messages.filter(
        (m) =>
          !(m.id.startsWith("temp-") && m.role === message.role && m.content === message.content),
      );
      return { messages: orderMessages([...filtered, message]), streamingByThread };
    });
  },

  updateThreadStatus: (
    threadId,
    status,
    phase,
    toolName,
    activePlan,
    pendingQuestion,
    planModeEnabled,
    taskList,
  ) => {
    set((s) => ({
      threads: s.threads.map((t) =>
        t.id === threadId
          ? {
              ...t,
              status: status as ChatThread["status"],
              current_phase: phase as ChatThread["current_phase"],
              ...(planModeEnabled !== undefined ? { plan_mode_enabled: planModeEnabled } : {}),
            }
          : t,
      ),
    }));

    // Track per-thread plan/question snapshots from the broadcast payload.
    if (activePlan !== undefined) {
      set((s) => ({
        plansByThread: { ...s.plansByThread, [threadId]: activePlan },
      }));
    }
    if (pendingQuestion !== undefined) {
      set((s) => ({
        questionsByThread: { ...s.questionsByThread, [threadId]: pendingQuestion },
      }));
    }
    if (taskList !== undefined) {
      set((s) => ({
        taskListByThread: { ...s.taskListByThread, [threadId]: taskList },
      }));
    }

    // A finished turn has nothing left to stream; drop any partial buffer
    // (the resync below fetches the real message if its broadcast was lost).
    if (status === "completed" || status === "error" || status === "interrupted") {
      get().clearStreaming(threadId);
    }

    // When a thread completes (or errors), catch up on any messages whose
    // broadcast was missed (subscription not yet ready, socket dropped).
    const { currentThreadId } = get();
    if (currentThreadId === threadId && status === "completed") {
      noteTurnRendered(get, set, threadId, "broadcast");
    }
    if (currentThreadId === threadId && (status === "completed" || status === "error")) {
      void get().resyncThread(threadId);
    }

    // Bump dataVersion when a tool finishes (phase goes back to "thinking")
    // or thread completes — views subscribe to this to trigger refetches
    if (phase === "thinking" || status === "completed") {
      set((s) => ({ dataVersion: s.dataVersion + 1 }));
    }

    void toolName;
  },

  updateThreadTitle: (threadId, title) => {
    set((s) => ({
      threads: s.threads.map((t) => (t.id === threadId ? { ...t, title } : t)),
    }));
  },

  setPlanModeEnabled: (threadId, enabled) => {
    set((s) => ({
      threads: s.threads.map((t) => (t.id === threadId ? { ...t, plan_mode_enabled: enabled } : t)),
    }));
  },

  setConnectionState: (state) => {
    if (get().connectionState !== state) set({ connectionState: state });
  },

  appendStreamDelta: (threadId, messageId, seq, text) => {
    set((s) => {
      const current = s.streamingByThread[threadId];
      const continues = current !== undefined && current.messageId === messageId;
      if (!continues && seq !== 1) {
        // Joined mid-stream (a reconnect, or a new stream whose first delta
        // was lost): wait for the persisted message instead.
        return current ? { streamingByThread: withoutKey(s.streamingByThread, threadId) } : s;
      }
      if (continues && seq !== current.seq + 1) {
        if (seq <= current.seq) return s;
        return { streamingByThread: withoutKey(s.streamingByThread, threadId) };
      }
      const reply: StreamingReply = {
        messageId,
        seq,
        text: continues ? current.text + text : text,
        lastDeltaAt: Date.now(),
      };
      return { streamingByThread: { ...s.streamingByThread, [threadId]: reply } };
    });
  },

  clearStreaming: (threadId) => {
    set((s) =>
      threadId in s.streamingByThread
        ? { streamingByThread: withoutKey(s.streamingByThread, threadId) }
        : s,
    );
  },

  acceptBroadcast: (threadId, seq) => {
    if (seq === undefined) return true;
    const last = get().lastSeqByThread[threadId];
    if (last !== undefined && seq <= last) return false;
    set((s) => ({ lastSeqByThread: { ...s.lastSeqByThread, [threadId]: seq } }));
    if (last !== undefined && seq > last + 1 && get().currentThreadId === threadId) {
      track("seq_gap", { thread_id: threadId, expected_seq: last + 1, received_seq: seq });
      if (gapResyncTimer !== undefined) clearTimeout(gapResyncTimer);
      gapResyncTimer = setTimeout(() => {
        gapResyncTimer = undefined;
        void get().resyncThread(threadId);
      }, RESYNC_GAP_DEBOUNCE_MS);
    }
    return true;
  },

  fetchThreads: async () => {
    set({ isLoading: true });
    const campaignId = get().viewContext.campaign_id;
    try {
      const { threads, session_id } = await chatApi.fetchThreads(campaignId);
      // Guard against a campaign switch mid-flight: a slow response for the
      // previous campaign must not clobber the list the user is now viewing.
      if (get().viewContext.campaign_id !== campaignId) return;
      set({ threads, sessionId: session_id, isLoading: false });
    } catch (e) {
      console.error("Failed to fetch threads:", e);
      if (get().viewContext.campaign_id !== campaignId) return;
      set({ isLoading: false });
    }
  },

  fetchMessages: async (threadId) => {
    set((s) => ({ isLoading: true, streamingByThread: withoutKey(s.streamingByThread, threadId) }));
    try {
      const { thread, messages, active_plan, pending_question, task_list } =
        await chatApi.fetchThread(threadId);
      // Guard against a thread switch mid-flight: a slow response for thread A
      // must never land in the global messages slot once the user has moved to
      // thread B (which would render A's transcript under B's header).
      if (get().currentThreadId !== threadId) return;
      set((s) => ({
        messages,
        isLoading: false,
        plansByThread: { ...s.plansByThread, [threadId]: active_plan ?? null },
        questionsByThread: { ...s.questionsByThread, [threadId]: pending_question ?? null },
        taskListByThread: { ...s.taskListByThread, [threadId]: task_list ?? [] },
        lastSeqByThread: {
          ...s.lastSeqByThread,
          [threadId]: Math.max(s.lastSeqByThread[threadId] ?? 0, thread?.broadcast_seq ?? 0),
        },
      }));
    } catch (e) {
      console.error("Failed to fetch messages:", e);
      if (get().currentThreadId !== threadId) return;
      // 404 means the persisted thread id points at a thread that no longer
      // exists (deleted, or from a previous database) — clear it so every
      // subsequent page load doesn't re-request a dead thread.
      if ((e as { status?: number }).status === 404) {
        localStorage.removeItem("chat-thread-id");
        set({ currentThreadId: null, messages: [], isLoading: false });
        return;
      }
      set({ isLoading: false });
    }
  },

  resyncThread: async (threadId) => {
    if (get().currentThreadId !== threadId) return;
    const persisted = get().messages.filter((m) => !m.id.startsWith("temp-"));
    const after = persisted.length > 0 ? persisted[persisted.length - 1].id : undefined;
    try {
      const { thread, messages, active_plan, pending_question, task_list } =
        await chatApi.fetchMessagesAfter(threadId, after);
      if (get().currentThreadId !== threadId) return;
      set((s) => {
        const known = new Set(s.messages.map((m) => m.id));
        const fresh = messages.filter((m) => !known.has(m.id));
        // Optimistic temps superseded by a fetched message are dropped with
        // the same rule addMessage uses; unmatched temps (failed sends)
        // stay at the tail.
        const kept = s.messages.filter(
          (m) =>
            !(
              m.id.startsWith("temp-") &&
              fresh.some((f) => f.role === m.role && f.content === m.content)
            ),
        );
        const keptPersisted = kept.filter((m) => !m.id.startsWith("temp-"));
        const keptTemps = kept.filter((m) => m.id.startsWith("temp-"));
        const previous = s.threads.find((t) => t.id === threadId);
        const completedNow = thread.status === "completed" && previous?.status !== "completed";
        return {
          messages: orderMessages([...keptPersisted, ...fresh, ...keptTemps]),
          threads: s.threads.map((t) => (t.id === threadId ? { ...t, ...thread } : t)),
          // Whatever was streaming is either in `fresh` now or still on its
          // way as a full message; a partial buffer is no longer trustworthy.
          streamingByThread: withoutKey(s.streamingByThread, threadId),
          plansByThread: { ...s.plansByThread, [threadId]: active_plan ?? null },
          questionsByThread: { ...s.questionsByThread, [threadId]: pending_question ?? null },
          taskListByThread: { ...s.taskListByThread, [threadId]: task_list ?? [] },
          lastSeqByThread: {
            ...s.lastSeqByThread,
            [threadId]: Math.max(s.lastSeqByThread[threadId] ?? 0, thread.broadcast_seq ?? 0),
          },
          // A completion we only learned about by resyncing still has to
          // fan out to the views that refetch on chat-driven changes.
          dataVersion: completedNow ? s.dataVersion + 1 : s.dataVersion,
        };
      });
      if (thread.status === "completed") noteTurnRendered(get, set, threadId, "resync");
    } catch (e) {
      console.error("Failed to resync thread:", e);
      if (get().currentThreadId !== threadId) return;
      if ((e as { status?: number }).status === 404) {
        localStorage.removeItem("chat-thread-id");
        set({ currentThreadId: null, messages: [] });
      }
    }
  },

  createThread: async (message, attachments, planModeEnabled, richTextArtefacts) => {
    set({ isSending: true });
    try {
      const context = buildContextPayload(get().viewContext);
      const apiAttachments = attachments
        ?.filter((a) => a.signed_id && !a.error)
        .map(({ signed_id, filename, content_type }) => ({ signed_id, filename, content_type }));
      const apiArtefacts = richTextArtefacts?.map(({ label, size_bytes, markdown }) => ({
        label,
        size_bytes,
        markdown,
      }));
      const thread = await chatApi.createThread(
        message,
        context,
        apiAttachments,
        planModeEnabled,
        apiArtefacts,
      );
      localStorage.setItem("chat-thread-id", thread.id);
      set((s) => ({
        threads: [thread, ...s.threads],
        currentThreadId: thread.id,
        messages: [
          {
            id: `temp-${Date.now()}`,
            thread_id: thread.id,
            role: "user",
            content: message || optimisticPlaceholder(apiAttachments?.length, apiArtefacts?.length),
            attachments,
            rich_text_artefacts: richTextArtefacts,
            created_at: new Date().toISOString(),
          },
        ],
        isSending: false,
      }));
      // The create request persists the first user message itself, and its
      // broadcast fires while this tab is still on the previous thread, so
      // addMessage drops it. Without the persisted row the optimistic temp
      // above is never superseded and, temps being pinned to the tail, every
      // reply sorts above the user's own message for the life of the tab.
      // Pull the transcript now: the fetched message replaces the temp by
      // the usual role+content rule and ordering follows server time.
      await get().resyncThread(thread.id);
    } catch (e) {
      reportError(e, "starting the chat");
      set({ isSending: false });
    }
  },

  sendMessage: async (threadId, message, attachments, richTextArtefacts) => {
    set({ isSending: true });
    const apiAttachments = attachments
      ?.filter((a) => a.signed_id && !a.error)
      .map(({ signed_id, filename, content_type }) => ({ signed_id, filename, content_type }));
    const apiArtefacts = richTextArtefacts?.map(({ label, size_bytes, markdown }) => ({
      label,
      size_bytes,
      markdown,
    }));
    // Optimistically add the user message. Keep the temp id so the failure
    // handler can mark exactly this bubble as failed and offer a retry.
    const tempId = `temp-${Date.now()}`;
    const tempMessage: ChatMessage = {
      id: tempId,
      thread_id: threadId,
      role: "user",
      content: message || optimisticPlaceholder(apiAttachments?.length, apiArtefacts?.length),
      attachments,
      rich_text_artefacts: richTextArtefacts,
      created_at: new Date().toISOString(),
    };
    set((s) => ({
      messages: [...s.messages, tempMessage],
      taskListByThread: { ...s.taskListByThread, [threadId]: [] },
      streamingByThread: withoutKey(s.streamingByThread, threadId),
    }));
    try {
      const context = buildContextPayload(get().viewContext);
      const result = await chatApi.sendMessage(
        threadId,
        message,
        context,
        apiAttachments,
        apiArtefacts,
      );
      // Apply the returned thread (now `queued`) so the waiting state shows
      // even if the status_update broadcast never reaches this tab. The
      // response is a snapshot taken when the message was accepted: if the
      // socket has already delivered newer state (a fast turn finishing
      // before the response rendered) the snapshot is stale and is dropped,
      // like any other out-of-order update.
      const thread = result?.thread;
      set((s) => {
        const seq = thread?.broadcast_seq;
        const last = s.lastSeqByThread[threadId];
        const apply =
          thread !== undefined && (seq === undefined || last === undefined || seq >= last);
        return {
          isSending: false,
          threads: apply
            ? s.threads.map((t) => (t.id === threadId ? { ...t, ...thread } : t))
            : s.threads,
          lastSeqByThread:
            apply && seq !== undefined
              ? { ...s.lastSeqByThread, [threadId]: Math.max(seq, last ?? 0) }
              : s.lastSeqByThread,
        };
      });
    } catch (e) {
      reportError(e, "sending your message");
      track("send_failed", { thread_id: threadId, status: (e as { status?: number }).status ?? 0 });
      // The message never persisted: mark the optimistic bubble as failed and
      // record enough to re-send it (the raw `message` text, not the display
      // placeholder that attachments substitute in). The bubble only lives in
      // the current thread's list, so mapping by id is a no-op if the user has
      // since switched threads.
      set((s) => ({
        isSending: false,
        messages: s.messages.map((m) =>
          m.id === tempId ? { ...m, status: "failed" as const } : m,
        ),
        failedSends: {
          ...s.failedSends,
          [tempId]: { threadId, message, attachments, richTextArtefacts },
        },
      }));
    }
  },

  interruptThread: async (threadId) => {
    try {
      const thread = await chatApi.interruptThread(threadId);
      set((s) => ({
        threads: s.threads.map((t) => (t.id === threadId ? thread : t)),
      }));
    } catch (e) {
      reportError(e, "stopping the agent");
    }
  },

  compactThread: async (threadId) => {
    try {
      const thread = await chatApi.compactThread(threadId);
      set((s) => ({
        threads: s.threads.map((t) => (t.id === threadId ? thread : t)),
      }));
      // Refetch messages to show the compacted state
      get().fetchMessages(threadId);
    } catch (e) {
      reportError(e, "compacting the conversation");
    }
  },

  retryMessage: async (tempId) => {
    const payload = get().failedSends[tempId];
    if (!payload) return;
    track("send_retried", { thread_id: payload.threadId });
    // Drop the failed bubble and its retry record before re-sending, so a
    // successful retry (whose real message arrives via the channel) never
    // leaves both the failed placeholder and the delivered message.
    set((s) => {
      const { [tempId]: _removed, ...rest } = s.failedSends;
      return {
        messages: s.messages.filter((m) => m.id !== tempId),
        failedSends: rest,
      };
    });
    await get().sendMessage(
      payload.threadId,
      payload.message,
      payload.attachments,
      payload.richTextArtefacts,
    );
  },
}));

// Persisted messages render in server order (created_at), whatever order they
// reached this tab. A resync that started before the assistant's broadcast
// landed used to append the user's own message *after* the reply (#3169);
// arrival order is not thread order. Optimistic temps (no server timestamp
// yet) stay at the tail, where they were appended.
function orderMessages(messages: ChatMessage[]): ChatMessage[] {
  const persisted = messages.filter((m) => !m.id.startsWith("temp-"));
  const temps = messages.filter((m) => m.id.startsWith("temp-"));
  const sorted = persisted
    .map((m, index) => ({ m, index }))
    .sort((a, b) => {
      const byTime = a.m.created_at.localeCompare(b.m.created_at);
      return byTime !== 0 ? byTime : a.index - b.index;
    })
    .map(({ m }) => m);
  return [...sorted, ...temps];
}

// Reports that this tab has rendered the completed turn (#3161) — once per
// user message, whether the completion arrived by broadcast or by resync. The
// join key is the last persisted user message, which is what ChatTurnMetric
// records at enqueue time. Optimistic temps are skipped: they have no id the
// server knows.
function noteTurnRendered(
  get: () => ChatState,
  set: (partial: Partial<ChatState> | ((s: ChatState) => Partial<ChatState>)) => void,
  threadId: string,
  via: "broadcast" | "resync",
): void {
  const s = get();
  const userMessage = [...s.messages]
    .reverse()
    .find((m) => m.role === "user" && !m.tool_result && !m.id.startsWith("temp-"));
  if (!userMessage) return;
  if (s.renderedTurnByThread[threadId] === userMessage.id) return;
  set((state) => ({
    renderedTurnByThread: { ...state.renderedTurnByThread, [threadId]: userMessage.id },
  }));
  track("turn_rendered", { thread_id: threadId, message_id: userMessage.id, via });
}

// Mirrors backend placeholder logic in Chat::MessagesController#create for the
// optimistic message bubble. Prefers the file placeholder when both attachments
// and rich-text artefacts are present (matches server precedence).
function optimisticPlaceholder(fileCount?: number, artefactCount?: number): string {
  if (fileCount && fileCount > 0) return `[Attached ${fileCount} file(s)]`;
  if (artefactCount && artefactCount > 0) return "[Pasted document]";
  return "";
}

function buildContextPayload(ctx: ViewContext): Record<string, string> {
  const payload: Record<string, string> = { view: ctx.view };
  if (ctx.resource_type) payload.resource_type = ctx.resource_type;
  if (ctx.resource_id) payload.resource_id = ctx.resource_id;
  if (ctx.campaign_id) payload.campaign_id = ctx.campaign_id;
  if (ctx.outreach_programme_id) payload.outreach_programme_id = ctx.outreach_programme_id;
  return payload;
}
